import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Mail, Phone, Calendar, Target, BookOpen, 
  CheckCircle, XCircle, Clock, BarChart3, MessageSquare, Plus, CheckSquare,
  Edit, Trash2, X, Save, Crown, Zap, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionComments, setSessionComments] = useState({}); // {sessionId: [comments]}
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sessions');
  const [showCommentForm, setShowCommentForm] = useState(null);
  const [commentData, setCommentData] = useState({ content: '', is_task: false, task_due_date: '' });
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [upgradingPro, setUpgradingPro] = useState(false);
  
  // Coach assignment
  const [coaches, setCoaches] = useState([]);
  const [showCoachModal, setShowCoachModal] = useState(false);
  const [assigningCoach, setAssigningCoach] = useState(false);

  useEffect(() => {
    fetchClientData();
    fetchCoaches();
  }, [clientId]);

  const fetchCoaches = async () => {
    try {
      const response = await API.get('/admin/coaches');
      setCoaches(response.data);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    }
  };

  const fetchClientData = async () => {
    try {
      const [clientRes, sessionsRes] = await Promise.all([
        API.get(`/admin/clients/${clientId}`),
        API.get(`/admin/clients/${clientId}/sessions`)
      ]);
      setClient(clientRes.data);
      const sessionsData = sessionsRes.data;
      setSessions(sessionsData);
      
      // Fetch comments for each session
      const commentsPromises = sessionsData.map(session => 
        API.get(`/sessions/${session.id}/comments`).catch(() => ({ data: [] }))
      );
      const commentsResults = await Promise.all(commentsPromises);
      
      const commentsMap = {};
      sessionsData.forEach((session, index) => {
        commentsMap[session.id] = commentsResults[index].data;
      });
      setSessionComments(commentsMap);
    } catch (error) {
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      await API.post(`/admin/clients/${clientId}/approve`);
      toast.success('Client approved!');
      fetchClientData();
    } catch (error) {
      toast.error('Failed to approve client');
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this client?')) return;
    try {
      await API.post(`/admin/clients/${clientId}/reject`);
      toast.success('Client rejected');
      fetchClientData();
    } catch (error) {
      toast.error('Failed to reject client');
    }
  };

  const startEditing = () => {
    setEditForm({
      full_name: client.full_name || '',
      email: client.email || '',
      phone: client.phone || '',
      handicap: client.handicap || '',
      status: client.status || 'pending'
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const updateData = {};
      if (editForm.full_name !== client.full_name) updateData.full_name = editForm.full_name;
      if (editForm.email !== client.email) updateData.email = editForm.email;
      if (editForm.phone !== (client.phone || '')) updateData.phone = editForm.phone || null;
      if (editForm.handicap !== (client.handicap || '')) updateData.handicap = editForm.handicap ? parseFloat(editForm.handicap) : null;
      if (editForm.status !== client.status) updateData.status = editForm.status;

      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        setIsEditing(false);
        return;
      }

      await API.put(`/admin/clients/${clientId}`, updateData);
      toast.success('Client updated successfully!');
      setIsEditing(false);
      fetchClientData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!window.confirm(`Are you sure you want to delete ${client.full_name}?\n\nThis will permanently delete:\n• All sessions\n• All game stats\n• All diary entries\n• All messages\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      await API.delete(`/admin/clients/${clientId}`);
      toast.success('Client deleted');
      navigate('/admin/clients');
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleUpgradeToPro = async () => {
    if (!window.confirm(`Upgrade ${client.full_name} to Pro?\n\nThis grants full Player's Locker access for 1 year (renewable) and emails them the good news. Cancel to keep them on the current tier.`)) {
      return;
    }
    
    setUpgradingPro(true);
    try {
      await API.put(`/admin/clients/${clientId}/subscription`, null, {
        params: { tier: 'pro' }
      });
      toast.success(`${client.full_name} upgraded to Pro!`);
      fetchClientData();
    } catch (error) {
      toast.error('Failed to upgrade client');
    } finally {
      setUpgradingPro(false);
    }
  };

  const handleDowngradeToFree = async () => {
    if (!window.confirm(`Downgrade ${client.full_name} to Free tier?\n\nThis will apply usage limits.`)) {
      return;
    }
    
    setUpgradingPro(true);
    try {
      await API.put(`/admin/clients/${clientId}/subscription`, null, {
        params: { tier: 'free' }
      });
      toast.success(`${client.full_name} downgraded to Free tier`);
      fetchClientData();
    } catch (error) {
      toast.error('Failed to downgrade client');
    } finally {
      setUpgradingPro(false);
    }
  };

  const handleAssignCoach = async (coachId) => {
    setAssigningCoach(true);
    try {
      await API.put(`/admin/clients/${clientId}/assign-coach`, null, {
        params: { coach_id: coachId || null }
      });
      toast.success(coachId ? 'Coach assigned!' : 'Coach unassigned');
      setShowCoachModal(false);
      fetchClientData();
    } catch (error) {
      toast.error('Failed to assign coach');
    } finally {
      setAssigningCoach(false);
    }
  };

  const handleAddComment = async (sessionId) => {
    if (!commentData.content.trim()) return;
    setSubmittingComment(true);
    try {
      const response = await API.post(`/admin/sessions/${sessionId}/comments`, {
        session_id: sessionId,
        content: commentData.content,
        is_task: commentData.is_task,
        task_due_date: commentData.task_due_date || null
      });
      toast.success(commentData.is_task ? 'Task assigned!' : 'Comment added!');
      
      // Update local comments state
      setSessionComments(prev => ({
        ...prev,
        [sessionId]: [response.data, ...(prev[sessionId] || [])]
      }));
      
      setShowCommentForm(null);
      setCommentData({ content: '', is_task: false, task_due_date: '' });
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-20">
        <p className="text-[#737373]">Client not found</p>
        <Link to="/admin/clients" className="text-[#BC562B] hover:underline mt-4 inline-block">
          Back to Clients
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="client-detail-page">
      {/* Back Link */}
      <Link
        to="/admin/clients"
        className="inline-flex items-center gap-2 text-[#737373] hover:text-[#1A1A1A] mb-6"
        data-testid="back-link"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Clients
      </Link>

      {/* Client Header */}
      <div className="bg-white border border-[#E7E5E4] p-6 md:p-8 mb-8">
        {isEditing ? (
          /* Edit Mode */
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Edit Client</h2>
              <button onClick={cancelEditing} className="p-2 text-[#737373] hover:text-[#1A1A1A]">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Full Name</label>
                <input
                  type="text"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                  className="w-full"
                  data-testid="edit-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full"
                  data-testid="edit-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full"
                  placeholder="+64 27 410 8444"
                  data-testid="edit-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Handicap</label>
                <input
                  type="number"
                  step="0.1"
                  value={editForm.handicap}
                  onChange={(e) => setEditForm(prev => ({ ...prev, handicap: e.target.value }))}
                  className="w-full"
                  placeholder="e.g., 12.5"
                  data-testid="edit-handicap"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full"
                  data-testid="edit-status"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="flex items-center gap-2 px-6 py-3 bg-[#2C5530] text-white font-semibold hover:bg-[#1E3A21] transition-colors disabled:opacity-50"
                data-testid="save-edit-btn"
              >
                <Save className="w-5 h-5" />
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={cancelEditing}
                className="px-6 py-3 border border-[#E7E5E4] text-[#57534E] font-semibold hover:bg-[#F2F0E9] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* View Mode */
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-[#2C5530] text-white flex items-center justify-center flex-shrink-0">
                <User className="w-10 h-10" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A]">
                    {client.full_name}
                  </h1>
                  {client.status === 'pending' && (
                    <span className="flex items-center gap-1 px-3 py-1 bg-[#BC562B]/10 text-[#BC562B] text-sm font-semibold">
                      <Clock className="w-4 h-4" />
                      Pending Approval
                    </span>
                  )}
                  {client.status === 'approved' && (
                    <span className="flex items-center gap-1 px-3 py-1 bg-[#2C5530]/10 text-[#2C5530] text-sm font-semibold">
                      <CheckCircle className="w-4 h-4" />
                      Approved
                    </span>
                  )}
                  {client.status === 'rejected' && (
                    <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-600 text-sm font-semibold">
                      <XCircle className="w-4 h-4" />
                      Rejected
                    </span>
                  )}
                  {/* Subscription Tier Badge */}
                  {client.subscription_tier === 'pro' ? (
                    <span className="flex items-center gap-1 px-3 py-1 bg-[#BC562B]/10 text-[#BC562B] text-sm font-semibold">
                      <Crown className="w-4 h-4" />
                      Pro
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-3 py-1 bg-[#F2F0E9] text-[#737373] text-sm font-semibold">
                      Free Tier
                    </span>
                  )}
                  {/* Coach Badge */}
                  {client.coach_name && (
                    <span className="flex items-center gap-1 px-3 py-1 bg-[#2C5530]/10 text-[#2C5530] text-sm font-semibold">
                      <GraduationCap className="w-4 h-4" />
                      Coach: {client.coach_name}
                    </span>
                  )}
                </div>
                
                <div className="space-y-2 text-[#57534E]">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#737373]" />
                    {client.email}
                  </p>
                  {client.phone && (
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#737373]" />
                      {client.phone}
                    </p>
                  )}
                  <p className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#737373]" />
                    Registered {formatDate(client.created_at)}
                  </p>
                  {client.handicap && (
                    <p>Handicap: <strong>{client.handicap}</strong></p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {client.status === 'pending' && (
                <>
                  <button
                    onClick={handleApprove}
                    className="flex items-center gap-2 px-6 py-3 bg-[#2C5530] text-white font-semibold hover:bg-[#1E3A21] transition-colors"
                    data-testid="approve-btn"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Approve
                  </button>
                  <button
                    onClick={handleReject}
                    className="flex items-center gap-2 px-6 py-3 border border-red-300 text-red-600 font-semibold hover:bg-red-50 transition-colors"
                    data-testid="reject-btn"
                  >
                    <XCircle className="w-5 h-5" />
                    Reject
                  </button>
                </>
              )}
              {/* Subscription Management */}
              {client.status === 'approved' && (
                client.subscription_tier !== 'pro' ? (
                  <button
                    onClick={handleUpgradeToPro}
                    disabled={upgradingPro}
                    className="flex items-center gap-2 px-4 py-3 bg-[#BC562B] text-white font-semibold hover:bg-[#9A4520] transition-colors disabled:opacity-50"
                    data-testid="upgrade-pro-btn"
                  >
                    <Crown className="w-5 h-5" />
                    {upgradingPro ? 'Upgrading...' : 'Upgrade to Pro'}
                  </button>
                ) : (
                  <button
                    onClick={handleDowngradeToFree}
                    disabled={upgradingPro}
                    className="flex items-center gap-2 px-4 py-3 border border-[#E7E5E4] text-[#57534E] font-semibold hover:bg-[#F2F0E9] transition-colors disabled:opacity-50"
                    data-testid="downgrade-free-btn"
                  >
                    {upgradingPro ? 'Processing...' : 'Downgrade to Free'}
                  </button>
                )
              )}
              {/* Assign Coach Button */}
              {client.status === 'approved' && (
                <button
                  onClick={() => setShowCoachModal(true)}
                  className="flex items-center gap-2 px-4 py-3 border border-[#2C5530] text-[#2C5530] font-semibold hover:bg-[#2C5530]/5 transition-colors"
                  data-testid="assign-coach-btn"
                >
                  <GraduationCap className="w-5 h-5" />
                  {client.coach_id ? 'Change Coach' : 'Assign Coach'}
                </button>
              )}
              <button
                onClick={startEditing}
                className="flex items-center gap-2 px-4 py-3 border border-[#E7E5E4] text-[#57534E] font-semibold hover:bg-[#F2F0E9] transition-colors"
                data-testid="edit-client-btn"
              >
                <Edit className="w-5 h-5" />
                Edit
              </button>
              <button
                onClick={handleDeleteClient}
                className="flex items-center gap-2 px-4 py-3 border border-red-300 text-red-600 font-semibold hover:bg-red-50 transition-colors"
                data-testid="delete-client-btn"
              >
                <Trash2 className="w-5 h-5" />
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Coach Assignment Modal */}
        {showCoachModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowCoachModal(false)}></div>
            <div className="relative bg-white max-w-md w-full p-6 md:p-8" data-testid="coach-modal">
              <button
                onClick={() => setShowCoachModal(false)}
                className="absolute top-4 right-4 p-2 text-[#737373] hover:text-[#1A1A1A]"
              >
                <X className="w-5 h-5" />
              </button>

              <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Assign Coach</h2>
              <p className="text-[#57534E] mb-6">Select a coach for {client.full_name}</p>

              <div className="space-y-2 max-h-80 overflow-y-auto">
                {/* Unassign option */}
                {client.coach_id && (
                  <button
                    onClick={() => handleAssignCoach(null)}
                    disabled={assigningCoach}
                    className="w-full p-4 border border-red-200 text-left hover:bg-red-50 transition-colors disabled:opacity-50"
                    data-testid="unassign-coach"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0">
                        <X className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-red-600">Remove Coach</p>
                        <p className="text-sm text-red-500">Unassign current coach</p>
                      </div>
                    </div>
                  </button>
                )}

                {coaches.map((coach) => (
                  <button
                    key={coach.id}
                    onClick={() => handleAssignCoach(coach.id)}
                    disabled={assigningCoach || coach.id === client.coach_id}
                    className={`w-full p-4 border text-left transition-colors disabled:opacity-50 ${
                      coach.id === client.coach_id 
                        ? 'border-[#2C5530] bg-[#2C5530]/5' 
                        : 'border-[#E7E5E4] hover:border-[#2C5530]/30 hover:bg-[#F2F0E9]'
                    }`}
                    data-testid={`select-coach-${coach.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#2C5530] text-white flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-[#1A1A1A]">{coach.full_name}</p>
                        <p className="text-sm text-[#737373]">{coach.specialization || coach.email}</p>
                      </div>
                      {coach.id === client.coach_id && (
                        <span className="px-2 py-1 bg-[#2C5530] text-white text-xs font-semibold">
                          Current
                        </span>
                      )}
                    </div>
                  </button>
                ))}

                {coaches.length === 0 && (
                  <div className="text-center py-8">
                    <GraduationCap className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
                    <p className="text-[#57534E]">No coaches available</p>
                    <Link
                      to="/admin/coaches"
                      className="text-[#2C5530] font-semibold hover:underline mt-2 inline-block"
                    >
                      Create a coach account
                    </Link>
                  </div>
                )}
              </div>

              {assigningCoach && (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-[#57534E]">Assigning...</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-[#E7E5E4]">
          <div className="bg-[#F2F0E9] p-4">
            <Calendar className="w-6 h-6 text-[#2C5530] mb-2" />
            <p className="text-2xl font-bold text-[#1A1A1A]">{client.total_sessions}</p>
            <p className="text-sm text-[#737373]">Sessions</p>
          </div>
          <div className="bg-[#F2F0E9] p-4">
            <Target className="w-6 h-6 text-[#BC562B] mb-2" />
            <p className="text-2xl font-bold text-[#1A1A1A]">{client.total_games}</p>
            <p className="text-sm text-[#737373]">Games</p>
          </div>
          <div className="bg-[#F2F0E9] p-4">
            <BookOpen className="w-6 h-6 text-[#2C5530] mb-2" />
            <p className="text-2xl font-bold text-[#1A1A1A]">{client.total_diary_entries}</p>
            <p className="text-sm text-[#737373]">Diary Entries</p>
          </div>
          <div className="bg-[#F2F0E9] p-4">
            <BarChart3 className="w-6 h-6 text-[#BC562B] mb-2" />
            <p className="text-sm text-[#737373]">Last Login</p>
            <p className="font-semibold text-[#1A1A1A]">{formatDate(client.last_login)}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'sessions', label: 'Sessions', icon: Calendar },
          { id: 'reports', label: 'Reports', icon: BarChart3 }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-[#1A1A1A] text-white'
                : 'bg-white border border-[#E7E5E4] text-[#57534E] hover:border-[#1A1A1A]'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sessions Tab */}
      {activeTab === 'sessions' && (
        <div className="space-y-4">
          {sessions.length === 0 ? (
            <div className="text-center py-12 bg-white border border-[#E7E5E4]">
              <Calendar className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
              <p className="text-[#737373]">No sessions recorded yet</p>
            </div>
          ) : (
            sessions.map((session, index) => (
              <div
                key={session.id}
                className="bg-white border border-[#E7E5E4] p-6"
                data-testid={`session-${index}`}
              >
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className={`px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
                    session.session_type === 'coaching' ? 'bg-[#2C5530] text-white' :
                    session.session_type === 'playing' ? 'bg-[#BC562B] text-white' :
                    'bg-[#F2F0E9] text-[#57534E]'
                  }`}>
                    {session.session_type}
                  </span>
                  <span className="text-sm text-[#737373]">{formatDate(session.date)}</span>
                  <span className="text-sm text-[#737373]">{session.duration_minutes} min</span>
                </div>
                
                <p className="text-[#1A1A1A] mb-2">{session.notes}</p>
                
                {session.key_learnings && (
                  <p className="text-[#57534E] text-sm italic mb-3">"{session.key_learnings}"</p>
                )}

                {session.intention_for_session && (
                  <div className="bg-[#F2F0E9] p-3 mb-3">
                    <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Intention</p>
                    <p className="text-[#1A1A1A]">{session.intention_for_session}</p>
                  </div>
                )}

                {/* Existing Comments Display */}
                {sessionComments[session.id]?.length > 0 && (
                  <div className="bg-[#2C5530]/5 border border-[#2C5530]/20 p-4 mb-3">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4 text-[#2C5530]" />
                      <span className="text-xs uppercase tracking-wider text-[#2C5530] font-semibold">
                        Comments & Tasks ({sessionComments[session.id].length})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {sessionComments[session.id].map((comment, cIndex) => (
                        <div 
                          key={comment.id} 
                          className={`p-3 bg-white ${comment.is_task ? 'border-l-4 border-[#BC562B]' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              {comment.is_task && (
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs uppercase tracking-wider text-[#BC562B] font-semibold">Task</span>
                                  {comment.task_due_date && (
                                    <span className="text-xs text-[#737373]">Due: {formatDate(comment.task_due_date)}</span>
                                  )}
                                </div>
                              )}
                              <p className="text-[#1A1A1A] text-sm">{comment.content}</p>
                              <p className="text-xs text-[#737373] mt-1">
                                {formatDate(comment.created_at)}
                              </p>
                            </div>
                            {comment.is_task && (
                              <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 ${
                                comment.task_completed 
                                  ? 'bg-[#2C5530]/10 text-[#2C5530]' 
                                  : 'bg-[#BC562B]/10 text-[#BC562B]'
                              }`}>
                                {comment.task_completed ? (
                                  <><CheckCircle className="w-3 h-3" /> Done</>
                                ) : (
                                  <><Clock className="w-3 h-3" /> Pending</>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comment/Task Form */}
                {showCommentForm === session.id ? (
                  <div className="mt-4 p-4 bg-[#F2F0E9] border border-[#E7E5E4]">
                    <h4 className="font-semibold mb-3">Add Comment or Task</h4>
                    <textarea
                      value={commentData.content}
                      onChange={(e) => setCommentData({ ...commentData, content: e.target.value })}
                      placeholder="Write your comment or task..."
                      rows={3}
                      className="w-full mb-3"
                    />
                    <div className="flex items-center gap-4 mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={commentData.is_task}
                          onChange={(e) => setCommentData({ ...commentData, is_task: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">This is a task</span>
                      </label>
                      {commentData.is_task && (
                        <input
                          type="date"
                          value={commentData.task_due_date}
                          onChange={(e) => setCommentData({ ...commentData, task_due_date: e.target.value })}
                          className="text-sm"
                          placeholder="Due date"
                        />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAddComment(session.id)}
                        disabled={submittingComment}
                        className="px-4 py-2 bg-[#2C5530] text-white text-sm font-semibold hover:bg-[#1E3A21] disabled:opacity-50"
                      >
                        {submittingComment ? 'Sending...' : 'Send'}
                      </button>
                      <button
                        onClick={() => {
                          setShowCommentForm(null);
                          setCommentData({ content: '', is_task: false, task_due_date: '' });
                        }}
                        className="px-4 py-2 border border-[#E7E5E4] text-[#57534E] text-sm font-semibold hover:bg-white"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowCommentForm(session.id)}
                    className="flex items-center gap-2 text-[#2C5530] text-sm font-semibold hover:underline mt-3"
                    data-testid={`add-comment-btn-${index}`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Add Comment / Task
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="bg-white border border-[#E7E5E4] p-8 text-center">
          <BarChart3 className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
          <p className="text-[#737373] mb-2">Client Performance Reports</p>
          <p className="text-sm text-[#737373]">
            View {client.full_name}'s performance data including score trends, session statistics, and more.
          </p>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
